/**
 * Smart Review session builder — orders and selects cards for a review session.
 * Pure logic: takes candidates + options, returns ordered card ids and summary.
 */

import type { UserCardProgress } from "./types";
import type { SmartReviewOptions, SmartReviewPreset, SmartReviewSessionSummary, StudyModeKey } from "./types";
import { REVIEW_PRIORITY } from "./constants";
import { computeReviewPriority } from "./reviewPriority";
import { computeDifficultyMetrics } from "./difficulty";
import { computeMemoryStrength } from "./memory";
import { computeForgettingRisk } from "./memory";
import { computeNextReviewAt } from "./memory";
import { computeOverdueScore } from "./memory";
import { getRecommendedMode } from "./memory";

export interface SmartReviewCandidate {
  cardId: string;
  deckId: string;
  progress: UserCardProgress | null;
}

/**
 * Build ordered list of card ids for Smart Review.
 * - Sort by review_priority (desc)
 * - Cap consecutive critical/hard cards; inject easier cards as buffer
 * - Apply maxCards and preset limits
 */
export function buildSmartReviewSession(
  candidates: SmartReviewCandidate[],
  options: SmartReviewOptions,
  now: Date
): { cardIds: string[]; deckIdByCardId: Record<string, string>; summary: SmartReviewSessionSummary } {
  const maxCards = options.maxCards ?? 50;
  const preset = options.preset ?? "today";

  // Compute priority and difficulty for each candidate
  const withMeta = candidates.map((c) => {
    const progress = c.progress;
    let priority = 0;
    let level: "easy" | "medium" | "hard" | "critical" = "medium";
    if (progress) {
      const metrics = computeDifficultyMetrics(progress);
      level = metrics.difficultyLevel;
      priority = computeReviewPriority(progress, now);
    } else {
      priority = 100; // new cards get medium-high priority
    }
    return { cardId: c.cardId, deckId: c.deckId, progress, priority, level };
  });

  // Sort by priority descending
  withMeta.sort((a, b) => b.priority - a.priority);

  // Apply preset caps
  let take = maxCards;
  if (preset === "fast") take = Math.min(10, maxCards);
  else if (preset === "recovery") take = Math.min(30, maxCards);

  const cardIds: string[] = [];
  const deckIdByCardId: Record<string, string> = {};
  let consecutiveCritical = 0;
  let sinceLastEasy = 0;
  const seen = new Set<string>();

  for (const item of withMeta) {
    if (cardIds.length >= take) break;
    if (seen.has(item.cardId)) continue;
    seen.add(item.cardId);

    if (item.level === "critical" || item.level === "hard") {
      consecutiveCritical++;
      sinceLastEasy++;
      if (consecutiveCritical > REVIEW_PRIORITY.MAX_CONSECUTIVE_CRITICAL) {
        const easy = withMeta.find(
          (x) => (x.level === "easy" || x.level === "medium") && !seen.has(x.cardId)
        );
        if (easy) {
          cardIds.push(easy.cardId);
          deckIdByCardId[easy.cardId] = easy.deckId;
          seen.add(easy.cardId);
          consecutiveCritical = 0;
          sinceLastEasy = 0;
        }
      }
    } else {
      consecutiveCritical = 0;
      sinceLastEasy = 0;
    }

    cardIds.push(item.cardId);
    deckIdByCardId[item.cardId] = item.deckId;
  }

  const overdueCount = cardIds.filter((id) => {
    const m = withMeta.find((x) => x.cardId === id);
    return m?.progress && (m.progress.overdueScore ?? computeOverdueScore(m.progress, now)) > 0;
  }).length;
  const weakCount = cardIds.filter((id) => {
    const m = withMeta.find((x) => x.cardId === id);
    return m?.level === "hard" || m?.level === "critical";
  }).length;
  const newCount = cardIds.filter((id) => {
    const m = withMeta.find((x) => x.cardId === id);
    return !m?.progress || m.progress.timesSeen === 0;
  }).length;

  const hardOrNew = weakCount + newCount;
  const primaryMode: StudyModeKey =
    cardIds.length === 0
      ? "cards"
      : hardOrNew >= cardIds.length * 0.5
        ? "learn"
        : overdueCount >= cardIds.length * 0.3
          ? "cards"
          : "cards";
  const summary: SmartReviewSessionSummary = {
    overdueCount,
    weakCount,
    newCount,
    recommendedPrimaryMode: primaryMode,
    sessionType: preset,
    createdAt: now.toISOString(),
  };

  return { cardIds, deckIdByCardId, summary };
}

/**
 * Recompute all adaptive metrics for a progress row (for batch/on-demand recalc).
 */
export function recomputeAdaptiveMetrics(progress: UserCardProgress, now: Date): Partial<UserCardProgress> {
  const { difficultyScore, difficultyLevel } = computeDifficultyMetrics(progress);
  const memoryStrength = computeMemoryStrength(progress, now);
  const forgettingRisk = computeForgettingRisk({ ...progress, memoryStrength }, now);
  const nextReviewAt = computeNextReviewAt({ ...progress, memoryStrength }, now);
  const overdueScore = computeOverdueScore(progress, now);
  const stabilityScore = (() => {
    const seen = progress.timesSeen;
    if (seen < 2) return 50;
    const ratio = progress.timesCorrect / seen;
    const streakBonus = Math.min(30, progress.bestStreak * 3);
    return Math.round(Math.min(100, ratio * 70 + streakBonus));
  })();
  const reviewPriority = computeReviewPriority(
    { ...progress, difficultyScore, difficultyLevel, forgettingRisk, overdueScore },
    now
  );
  const recommendedMode = getRecommendedMode({ ...progress, difficultyScore, memoryStrength });

  return {
    difficultyScore,
    difficultyLevel,
    memoryStrength,
    forgettingRisk,
    stabilityScore,
    nextReviewAt,
    overdueScore,
    reviewPriority,
    recommendedMode,
    updatedAt: now.toISOString(),
  };
}
